import {useLoadBalance} from '@hconnect/tuc-processes/fields/loadBalance/useLoadBalance'
import {usePlusLoadExpected} from '@hconnect/tuc-processes/fields/plusLoad/usePlusLoadExpected'
import {useQuantity} from '@hconnect/tuc-processes/fields/quantity/useQuantity'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {FieldButton} from '../../FieldButton'

interface QuantityFieldButtonProps {
  onClick: () => void
  requiresApproval?: boolean
  isReadOnly?: boolean
  isChanged?: boolean
}

export const QuantityFieldButton: React.FC<QuantityFieldButtonProps> = ({
  onClick,
  requiresApproval = false,
  isReadOnly = false,
  isChanged = false
}) => {
  const {t} = useTranslation()
  const quantityReturn = useQuantity()
  const {plusLoad} = usePlusLoadExpected()
  const loadBalance = useLoadBalance()

  // Build display value based on quantity mode
  let displayValue: string
  if (quantityReturn.type === 'fullLoads') {
    const label =
      quantityReturn.fullLoads === 1
        ? t('orderIntake.rmc.steps.quantity.fullLoad', '{{count}} full load', {
            count: quantityReturn.fullLoads
          })
        : t('orderIntake.rmc.steps.quantity.fullLoads', '{{count}} full loads', {
            count: quantityReturn.fullLoads
          })
    const range = t('orderIntake.rmc.steps.quantity.fullLoadRange', '{{min}} – {{max}} {{uom}}', {
      min: quantityReturn.minQuantity,
      max: quantityReturn.maxQuantity,
      uom: quantityReturn.quantityConfiguration.uom
    })
    displayValue = `${label}: ${range}`
  } else {
    displayValue = plusLoad
      ? `${quantityReturn.volume} M³ + ${t('orderIntake.rmc.orderManagement.plusLoad', 'plus load')}`
      : `${quantityReturn.volume} M³`
  }

  // Append plus load for fullLoads mode too
  if (quantityReturn.type === 'fullLoads' && plusLoad) {
    displayValue += ` + ${t('orderIntake.rmc.orderManagement.plusLoad', 'plus load')}`
  }

  // Append split info when balance load is active (only relevant in quantity mode)
  if (quantityReturn.type === 'volume' && loadBalance.isEnabled && loadBalance.split) {
    const {split} = loadBalance
    displayValue += `\n${t(
      'orderIntake.rmc.loadBalance.splitLabel',
      '(split: {{first}}; {{second}})',
      {first: split.firstTruck, second: split.secondTruck}
    )}`
  }

  return (
    <FieldButton
      label={t('orderIntake.rmc.orderManagement.editSteps.labels.quantity', 'Quantity')}
      value={displayValue}
      onClick={onClick}
      requiresApproval={requiresApproval}
      isReadOnly={isReadOnly}
      isChanged={isChanged}
      data-test-id="quantity-field-button"
    />
  )
}
